import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Button, Card, Input } from '@monigo/ui';
import { ForgotPasswordSchema, type ForgotPasswordInput } from '@monigo/shared';
import { supabase } from '../../lib/supabase.js';
import { AuthLayout } from '../../components/Layout/AuthLayout.js';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(ForgotPasswordSchema) });

  return (
    <AuthLayout title={t('auth.forgotTitle')} subtitle={t('auth.resetSent')} backTo="/login">
      <div className="space-y-5">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-primary text-4xl text-white shadow-glow">
            🔐
          </div>
          <h1 className="text-4xl font-display font-extrabold text-text">
            {t('auth.forgotTitle')}
          </h1>
          <p className="mt-3 text-base leading-7 text-text-muted">{t('auth.resetSent')}</p>
        </div>

        <Card className="p-5 md:p-6">
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(async (values) => {
              await supabase.auth.resetPasswordForEmail(values.email, {
                redirectTo: `${window.location.origin}/login`,
              });
              setSent(true);
            })}
          >
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-semibold text-text">{t('auth.email')}</span>
              <Input
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                hasError={!!errors.email}
                {...register('email')}
              />
              {errors.email ? (
                <span className="text-xs text-danger">{errors.email.message}</span>
              ) : null}
            </label>
            <Button type="submit" isLoading={isSubmitting} className="w-full">
              {t('auth.resetCta')}
            </Button>
            {sent ? (
              <p className="rounded-[1.25rem] bg-success/10 p-4 text-center text-sm text-success">
                ✓ {t('auth.resetSent')}
              </p>
            ) : null}
          </form>
        </Card>

        <p className="text-center text-sm text-text-muted">
          <Link to="/login" className="font-semibold text-primary">
            {t('auth.loginCta')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
